import { store } from "~/state/store";
import { watch, watchMapset } from "~/state/util";
import { Unsubscribe } from "redux";
import { MAP_OVERLAYS } from "~/state/map/constants";
import Scene from "~/scene";
import { IMapState } from "~/state/map/interfaces";
import { addListener } from "~/lib/signals";
import { CONFIG } from "~/config";

interface IOverlay {
	layer: any;
	type: "middle" | "top";
}

/**
 * Sleduje změny zapnutých overlays ve store a změny map providera a pokud je na 2D mapě, tak vykresluje zapnuté overlays v předem daném pořadí.
 */
export default class MapsetOverlays {
	#storeUnsubscribe: Unsubscribe;

	/** vrstva pro dostupnost panoramat */
	#panoramaLayer: any;

	/** vrstva pro bezky */
	#skiAlpLayer: any;

	/** vrstva pro dlazdice s daty katastru */
	#cadastralLayer: any;

	/** vrstva pro turisticke trasy a cyklotrasy */
	#turistLayer: any;

	#overlaysToLayers: {
		[MAP_OVERLAYS.TURIST]: IOverlay;
		[MAP_OVERLAYS.CADASTRAL]: IOverlay;
		[MAP_OVERLAYS.HILLSHADE]: IOverlay;
		[MAP_OVERLAYS.PANORAMA]: IOverlay;
	};

	#enablePanorama: boolean;

	constructor() {
		this.#panoramaLayer = Scene.createPanoLayer("pano-layer", {
			pointsUrl: `${CONFIG.mapserver.url}/panorama_pt_hybrid-m/`,
			lineUrl: `${CONFIG.mapserver.url}/panorama_ln_hybrid-m/`,
		});
		this.#skiAlpLayer = Scene.createLayerTile(null, `${CONFIG.mapserver.url}/slope-m/`, { fadeTime: 0 });
		this.#turistLayer = Scene.createLayerTile(null, `${CONFIG.mapserver.url}/hybrid-turist-m/`, { fadeTime: 0 });
		this.#cadastralLayer = Scene.createLayerTile(null, `${CONFIG.mapserver.url}/kn/`, {
			animTime: 0,
			prefetch: 0,
			alpha: true,
		});
		this.#enablePanorama = false;

		this.#overlaysToLayers = {
			[MAP_OVERLAYS.TURIST]: {
				layer: this.#turistLayer,
				type: "middle",
			},
			[MAP_OVERLAYS.CADASTRAL]: {
				layer: this.#cadastralLayer,
				type: "middle",
			},
			[MAP_OVERLAYS.HILLSHADE]: {
				layer: this.#skiAlpLayer,
				type: "top",
			},
			[MAP_OVERLAYS.PANORAMA]: {
				layer: this.#panoramaLayer,
				type: "top",
			},
		};

		// pridame overlaye ve spravnem poradi
		Scene.addLayer(this.#cadastralLayer);
		Scene.addLayer(this.#skiAlpLayer);
		Scene.addLayer(this.#panoramaLayer);

		const wt = watch(store.getState, "map", watchMapset);

		this.#storeUnsubscribe = store.subscribe(wt((newMapset, oldMapset) => {
			this.#setFromState(newMapset, oldMapset);
		}));

		Scene.addListener("provider-changed", data => {
			if (data.data.name === "map") {
				this.#setOverlaysFromStore();
			}
		});

		setTimeout(() => {
			this.#setOverlaysFromStore();
		}, 0);

		addListener(this, "print-page", "_printPage");
		addListener(this, "print-done", "_printDone");
		addListener(this, "pano-year", "_panoYear");
	}

	destroy() {
		this.#storeUnsubscribe?.();
	}

	#setOverlaysFromStore() {
		const state = store.getState().map;

		this.#setFromState(state);
	}

	#setFromState(newMapset: IMapState, oldMapset?: IMapState) {
		// chceme bilou
		if (["letecka"].indexOf(newMapset.mapset) === -1) {
			this.#cadastralLayer.setOptions({ className: "" });
		// chceme cernou
		} else {
			this.#cadastralLayer.setOptions({ className: "invert" });
		}

		Object.keys(this.#overlaysToLayers).map(overlay => parseInt(overlay)).forEach(overlay => {
			const oldOverlay = oldMapset ? oldMapset.mapset in oldMapset.currentOverlays && oldMapset.currentOverlays[oldMapset.mapset].indexOf(overlay) !== -1 : false;
			const newOverlay = newMapset.mapset in newMapset.currentOverlays && newMapset.currentOverlays[newMapset.mapset].indexOf(overlay) !== -1;

			if (oldOverlay !== newOverlay) {
				const layer = this.#overlaysToLayers[overlay.toString()].layer;

				if (newOverlay) {
					layer.appended && layer.enable();
				} else {
					layer.appended && layer.disable();
				}
			}
		});
	}

	bumpLayers(type: IOverlay["type"]) {
		Object.keys(this.#overlaysToLayers).map(overlay => parseInt(overlay)).forEach(overlay => {
			const currentOverlay = this.#overlaysToLayers[overlay.toString()];

			if (currentOverlay.type === type) {
				Scene.removeLayer(currentOverlay.layer);
				Scene.addLayer(currentOverlay.layer);
			}
		});
	}

	_printPage() {
		const state = store.getState();
		const { mapset } = state.map;

		this.#enablePanorama = mapset in state.map.currentOverlays && state.map.currentOverlays[mapset].indexOf(MAP_OVERLAYS.PANORAMA) !== -1;

		if (this.#enablePanorama) {
			this.#panoramaLayer.disable();
		}
	}

	_printDone() {
		if (this.#enablePanorama) {
			this.#panoramaLayer.enable();
		}

		this.#enablePanorama = false;
	}

	_panoYear(event) {
		this.#panoramaLayer.setLine(!!event.data.year);
	}
}
